import { Box, Typography } from "@mui/material";
import { useState } from "react";
import useColors from "../api/hooks/useColors";
import FilterChip from "../scenes/posture/scenarios/components/Header/FilterChip";
import { icons } from "../scenes/stack/environment/icons/iconExport";
import TextButton from "./buttons/TextButton";
import { Toast } from "./Toast";
import UploadCaption from "./UploadCaption";

const UploadFileArea = ({
  onFileSelect,
  label,
  caption = null,
  captionVariant = "info",
  acceptedFileTypes = ".xml",
  disabled = false,
  selectedFile = null,
  showDeleteIcon = true,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const colors = useColors();

  const validateFileType = (file) => {
    if (!file) return false;

    const fileName = file.name.toLowerCase();
    const acceptedTypes = acceptedFileTypes
      .split(",")
      .map((type) => type.trim().toLowerCase());

    const isValid = acceptedTypes.some((type) => {
      if (type.startsWith(".")) {
        return fileName.endsWith(type);
      }
      return fileName.includes(type);
    });

    if (!isValid) {
      Toast.error(
        `Invalid file type. Please select a file with extension: ${acceptedFileTypes}`
      );
    }

    return isValid;
  };

  const handleFileChange = (event) => {
    try {
      // Check if event exists and has the expected structure
      if (!event || !event.target) {
        return;
      }

      const files = event.target.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (validateFileType(file)) {
          if (onFileSelect) {
            onFileSelect(file);
          }
        }
      }
    } catch (error) {}
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (validateFileType(file)) {
        if (onFileSelect) {
          onFileSelect(file);
        }
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    // Only set drag over to false if we're leaving the drop area completely
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleReplace = () => {
    // Trigger file input click
    const input = document.createElement("input");
    input.type = "file";
    input.accept = acceptedFileTypes;
    input.onchange = handleFileChange;
    input.click();
  };

  const handleRemove = (_) => {
    if (onFileSelect) {
      onFileSelect(null);
    }
  };

  const handleButtonClick = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = acceptedFileTypes;
    input.onchange = (e) => {
      if (e && e.target) {
        handleFileChange(e);
      }
    };
    input.click();
  };

  const getBackgroundColor = () => {
    if (selectedFile) return colors.text_area_outlined_enabled_value_bg;
    if (isDragOver || isHovered) return colors.text_area_outlined_hovered_bg;
    return colors.text_area_outlined_enabled_value_bg;
  };

  const getBorderColor = () => {
    if (selectedFile) return colors.text_area_outlined_enabled_stroke;
    if (isDragOver || isHovered)
      return colors.text_area_outlined_hovered_stroke;
    return colors.text_area_outlined_enabled_stroke;
  };

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      {/* Label */}
      <Typography variant="subtitle2" color={colors.text_primary}>
        {label}
      </Typography>

      {/* Upload Area */}
      <Box
        border={`2px dashed ${getBorderColor()}`}
        borderRadius="8px"
        padding="24px"
        textAlign="center"
        sx={{
          backgroundColor: getBackgroundColor(),
          transition: "all 0.2s ease",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          width: "380px",
        }}
        onDrop={!disabled ? handleDrop : undefined}
        onDragOver={!disabled ? handleDragOver : undefined}
        onDragEnter={!disabled ? handleDragEnter : undefined}
        onDragLeave={!disabled ? handleDragLeave : undefined}
        onMouseEnter={() => !disabled && setIsHovered(true)}
        onMouseLeave={() => !disabled && setIsHovered(false)}
      >
        {selectedFile ? (
          // Uploaded state
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
          >
            <FilterChip
              label={selectedFile.name}
              handleDelete={handleRemove}
              handleOnCLick={() => {}}
              icon={icons.FileCodeIcon}
              sx={{
                display: "flex",
                flexShrink: 0,
                backgroundColor: colors.chip_enabled_bg,
                border: `0.5px solid ${colors.chip_enabled_stroke}`,
                borderRadius: "10px",
                height: "32px",
                padding: "0px 6px",
              }}
              showDeleteIcon={showDeleteIcon}
            />
            <TextButton
              onClick={handleReplace}
              color="primary"
              size="small"
              disabled={disabled}
            >
              Replace
            </TextButton>
          </Box>
        ) : (
          // Empty/Hover state
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={2}
            padding="0px 60px"
          >
            <Typography variant="body2" color={colors.text_secondary}>
              Upload or drag & drop a file
            </Typography>

            <TextButton
              onClick={handleButtonClick}
              color="primary"
              size="small"
              startIcon={icons.ExportIcon}
              disabled={disabled}
            >
              Upload file
            </TextButton>
          </Box>
        )}
      </Box>

      {/* Caption */}
      {caption && <UploadCaption variant={captionVariant} caption={caption} />}
    </Box>
  );
};

export default UploadFileArea;
